/**
 * InterviewProgress - Slim progress bar for public interview conversations.
 * Shows question progress, time remaining, and a finish button when wrapping up.
 */

import type { AIInterviewProgressData } from '@ispot/shared'

import { CheckCircle2, Clock, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface InterviewProgressProps {
  progress: AIInterviewProgressData | null
  startedAt: string | null
  estimatedDuration: number
  onFinish: () => void
}

export function InterviewProgress({
  progress,
  startedAt,
  estimatedDuration,
  onFinish,
}: InterviewProgressProps) {
  if (!progress) return null

  const { currentQuestionIndex, totalQuestions, currentTopic, pacingSignal } = progress
  const percent = Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)

  const elapsed = startedAt ? (Date.now() - new Date(startedAt).getTime()) / 60000 : 0
  const timeRemaining = Math.max(0, Math.round(estimatedDuration - elapsed))

  const isFinishing = pacingSignal === 'wrapping_up' || pacingSignal === 'complete'

  return (
    <div className="border-b border-warm-200 bg-white px-4 py-2">
      {/* Progress bar */}
      <div className="h-1 w-full rounded-full bg-warm-100">
        <div
          className="h-1 rounded-full bg-business transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Info row */}
      <div className="mt-1.5 flex items-center justify-between text-xs text-warm-500">
        <span className="flex items-center gap-1">
          <ChevronRight className="h-3 w-3" />
          Question {currentQuestionIndex + 1} of {totalQuestions}
          {currentTopic && (
            <span className="text-warm-400">&mdash; {currentTopic}</span>
          )}
        </span>

        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          ~{timeRemaining} min left
        </span>
      </div>

      {/* Finish button when wrapping up */}
      {isFinishing && (
        <Button
          onClick={onFinish}
          className="mt-2 w-full gap-1.5 bg-market text-white hover:bg-market/90"
          size="sm"
        >
          <CheckCircle2 className="h-4 w-4" />
          Finish Interview
        </Button>
      )}
    </div>
  )
}
